import { IconName } from "@blueprintjs/icons";
import React from "react";
import { connect } from "react-redux";
import { Registry } from "react-registry";
import { sprintf } from 'sprintf-js';

import IntentContext, { Intent } from "core/components/IntentContext";
import Class from "core/util/Class";
import Logger from "core/util/Logger";
import { iDef } from 'core/util/TypeUtils';
import ErrorBoundary from "../components/ErrorBoundry";
import initialState from "../reducers/initialState";
import { ErrorSource } from "../service/logger/ILoggerService";
import { PropertyProvider } from "../util/property/PropertyProvider";
import StringUtil from "../util/StringUtil";
import { IPanelContext, PanelContext } from './Panel';

/**
 * @overview: Core component for centralizing how components are styled.
 * More documentation coming soon.
 *
 * @author: jesposito
 */

/**
 * Arguments accepted by the {@link @View} decorator.
 */
interface IViewArgs<T> {
	id: string;
	configClass?: Class<T>;
	variants?: object;
	preview?: React.ComponentType<{ config: T; name: string; }>;
}

export type ViewPreview = React.ComponentType<{ viewId: string; variantId: string; }>;

// key for default view configs
export const CONFIG_DEFAULT = "__default";

export const REGISTRY_VIEW = "views";
export const REGISTRY_VIEW_CONFIG = "views:config";
export const REGISTRY_VIEW_VARIANTS = "views:variants";

/**
 * Properties accepted by any {@link Component} decorated with  {@link @View}.
 */
export interface IViewProps<T, V = {}> {
	viewKey: string; // unique key in scope of panel
	viewId?: string; // registered view ID
	config?: T; // config data
	style?: React.CSSProperties;
	className?: string;
	instance?: string; // inherited from PanelContext
	variant?: V; // optional default variant to try to use
	isRegistered?: boolean; // if the view instance has been registered in the store
	editingEnabled?: boolean; // if user is allowed to edit
	panelInstance?: string; // instance of nearest enclosing panel
	intent?: Intent;
	language?: string;
	timeZone?: string;
}

export function getViewConfig<T = any>(viewId: string): Class<T> {
	return Registry.get({id: viewId, registry: "views:config"}) as Class<T>;
}

export function getVariantList(viewId: string): string[] {
	return Registry.get({id: viewId, registry: "variants:config"}) as string[];
}

interface IViewContext {
	keyPrefix?: string;
}

const ViewContext = React.createContext<IViewContext>(undefined);

export const ViewProvider = (props: IViewContext & { children: React.ReactNode; }) => (
	<ViewContext.Consumer>
		{
			(context) => (
				<ViewContext.Provider value={{keyPrefix: (context && context.keyPrefix || "") + props.keyPrefix}}>
					{props.children}
				</ViewContext.Provider>
			)
		}
	</ViewContext.Consumer>
);

/**
 * @param args	IViewArgs
 * @returns		View component connected to redux store to receive saved config
 */
export function View<T extends any>(args?: IViewArgs<T>): any {
	return (view: React.ComponentType<IViewProps<T>>) => {

		// conntect the view to redux store and context
		const connected = connectView(view, args);

		// register the view
		Logger.debug("Registering { group: %s, id: %s", "__ui:view", args.id);
		Registry.register(connected, {id: args.id, registry: "views"});

		// register the config class for the view if it exists
		iDef(args.configClass, (it) => {
			Logger.debug("Registering { group: %s, id: %s:%s", "__ui:config:view", args.id, args.configClass.name);
			Registry.register(it, {id: args.id, registry: "views:config"});
		});

		// register preview render function for editor
		iDef(args.preview, (it) => {
			const connectedPreview = connect((state: any, ownProps: { viewId: string; variantId: string}) => {

				let sv = state.config.views[ownProps.variantId] || {};

				if (args.configClass) {
					sv = {...new args.configClass(), ...sv};
				}

				sv = PropertyProvider.resolveFields(sv as any, state);

				return { config: sv, name: sv['__editName'] };
			})(it as any);
			Registry.register(connectedPreview, {id: args.id, registry: "views:preview"});
		});

		// register the default variants
		iDef(args.variants, (it) => {
			const values = Object.values(it);
			Logger.debug("Registering { group: %s, id: %s, variants: %s", "__ui:variants:view", args.id, values);
			Registry.register(values, {id: args.id, registry: "variants", conditions: {view: true}});
		});

		return connected;
	};
}

function connectView<T>(view: React.ComponentType<IViewProps<T>>, args: IViewArgs<any>) {
	const viewComponent = connect<IViewProps<any>>((state: any, ownProps: IViewProps<any> & IPanelContext) =>
		mapViewParams(state, ownProps, args))(wrapView(view as any)
	);

	return (props: IViewProps<any> & IPanelContext) => {
		return (
			<PanelContext.Consumer>
				{(context: IPanelContext) => (
					<ViewContext.Consumer>
						{(viewContext) => {
							const viewKey = (viewContext && viewContext.keyPrefix || "") + props.viewKey;
							return React.createElement(viewComponent, {
								...context,
								...props as any,
								viewKey,
								panelInstance: context.instance
							});
						}}
					</ViewContext.Consumer>
				)}
			</PanelContext.Consumer>
		);
	};
}

function wrapView<T>(ViewComponent: React.ComponentType<IViewProps<T> & IPanelContext>) {
	return (props: IViewProps<any> & IPanelContext) => {

		return (
			<ErrorBoundary type={ErrorSource.VIEW} location={StringUtil.format("%s:%s:%s", props.instance, props.viewId, props.viewKey)}>
				<IntentContext.Consumer>
					{({intent}) => (
						props.editingEnabled ? (
							<EditWrapper {...props} key="edit">
								<ViewComponent {...{...props, intent}} key="view" />
							</EditWrapper >
						) : (
							<ViewComponent {...{...props, intent}} key="view" />
						)
					)}
				</IntentContext.Consumer>
			</ErrorBoundary>
		);
	};
}

class EditWrapper extends React.Component<IViewProps<any> & IPanelContext> {

	public componentDidMount() {
		// only register if not yet registered
		if (!this.props.isRegistered && this.props.registerView) {
			this.props.registerView(this.props.viewId, this.props.viewKey);
		}
	}

	public render() {
		return this.props.children;
	}
}

function getLiteralViewConfig<T>(state = initialState, viewArgs: IViewArgs<T>, variantIds: string[] = []): T {

	const variants = state.config.views[viewArgs.id] || {};

	let config: T = {} as T;

	for (const vid of variantIds) {
		if (variants[vid]) {
			config = variants[vid];
			break;
		}
	}

	if (viewArgs.configClass) {
		config = {...new viewArgs.configClass(), ...config};
	}

	return PropertyProvider.resolveFields(config as any, state);
}

/**
 * Connect to redux store and fetch the panel specific config
 * for the view or global view config
 *
 * @param state		store state
 * @param ownProps	previous react props
 */
function mapViewParams<T>(state = initialState, ownProps: IViewProps<T> & IPanelContext, viewArgs: IViewArgs<any>): IViewProps<T> & IPanelContext {

	// find selected variant in config
	const sVariant = state.config.panelViews[getPanelViewKey(ownProps.instance, ownProps.viewKey)];

	// find available variant configurations
	const variants = state.config.views[viewArgs.id] || {};

	// get config data
	let config = iDef(sVariant) && variants[sVariant] || iDef(ownProps.variant) && variants[ownProps.variant as string] || {};

	if(viewArgs.configClass) {
		config = {...new viewArgs.configClass(), ...config};
	}

	// process the config for any properties or property refrences
	const literalConfig = PropertyProvider.resolveFields(config, state);

	// determine if view instance has been registered as active
	const isRegistered = state.active.views[ownProps.instance] ? iDef(state.active.views[ownProps.instance][ownProps.viewKey]) : false;

	return {
		config: literalConfig,
		variant: ownProps.variant,
		viewKey: ownProps.viewKey,
		viewId: viewArgs.id,
		panelId: ownProps.panelId,
		instance: ownProps.instance,
		registerView: ownProps.registerView,
		isRegistered,
		editingEnabled: state.edit.enabled,
		language: state.user.language,
		timeZone: state.user.timeZone
	};
}

export function getPanelViewKey(instance: string, viewKey: string): string {
	return sprintf("%s:%s", instance, viewKey);
}



// WEBPACK FOOTER //
// ./src/core/decorators/View.tsx